import React from "react";
import { Link } from "react-router-dom";
import { styles } from "../../layout";
import FigureGrid from "../components/figuregrid";
import CustomLink from "../components/hoverlink";

import image1 from "./components/job/1.png";
import image2 from "./components/job/2.png";
import image3 from "./components/job/3.png";
import image4 from "./components/job/4.png";

const EquityResearchBlog: React.FC = () => {
  return (
    <div style={styles.pageContainer}>
      <h1 style={styles.header}>How I landed a job on Wall Street at 19</h1>
      <div style={styles.note}>Click here to watch this as a video</div>
      <p>
        In August of 2024 after a 10-week internship in NYC, I accepted a
        full-time job offer for a six-figure Equity Research position at BNP
        Paribas. I studied finance in college and had multiple other internships
        in finance. I want to share my experience landing this job from a
        non-target school and how someone in college can replicate the results.
      </p>

      <h2 style={styles.subHeader}>Overview of the Timeline</h2>
      <p>
        Applying/Networking → Interview for Internship → Summer Internship →
        Full-Time Offer
      </p>

      <h2 style={styles.subHeader}>Positioning Yourself to Get an Interview</h2>
      <p>
        In order to get an interview you need to have a few things in order.
        This includes your resume, LinkedIn, or other professional profiles in
        order to pass the initial screens by many of these companies or
        recruiters. In this blog, I will not go into too much detail about
        resumes I have made another blog{" "}
        <CustomLink
          href="/blog/how-to-create-a-resume"
          target="_blank"
          rel="noreferrer"
        >
          "How to Create a Resume"
        </CustomLink>{" "}
        that focuses on this in particular.{" "}
      </p>

      <h3 style={styles.subSubHeader}>Be Early</h3>
      <p>
        One thing that will boost your chances significantly is to be proactive
        in your internship search. If you are late in the game and scrambling to
        find an internship you are less likely to receive help from someone
        senior as they will not have the same perception of you as someone who
        contacted them 2 months earlier.
      </p>
      <p>
        The timeline for banking has continued to creep earlier and earlier. In
        some cases, you are landing an internship 1.5 years before you will ever
        hit the desk. In my case, I interviewed in April of my sophomore year,
        14 months before I would start my internship. This means you have to be
        thinking and have everything in order during your sophomore year, over a
        full year before your internship.
      </p>

      <h3 style={styles.subSubHeader}>“Sophomore Summits”</h3>
      <p>
        In my case, I was constantly searching my school's career center
        resources and job postings. We used Handshake, a recruiting platform
        that my school used. There I found a few “Sophomore Summits” which are
        1-2 days recruiting events for interested sophomores to learn more about
        the bank and engage with recruiters. I applied to the posting in early
        February and was able to get invited to the summit at BNP. (Some banks
        offer internships to sophomores that get them in the pipeline for
        junior-year roles as well.)
      </p>
      <p>
        I submitted my resume (this is why it's important to be on top of your
        resume early in college) and completed a pymetrics test (a platform to
        test your cognitive and emotional aptitude). This was not uncommon for
        insights programs or internships as you typically have to do initial
        screening via HireVue (a 1-way interview) or some computer software.
        Attending a Sophomore summit is the best way to get in the door without
        any extreme networking.
      </p>

      <h2 style={styles.subHeader}>Networking</h2>
      <p>
        Networking is the best thing you can do for yourself and helps you learn
        more about the field. During the month of February, I sent over 600
        LinkedIn requests to all sorts of bankers and finance professionals and
        had well over 50 Zoom/phone calls. There are a few key tips to make the
        networking process smoother that I will outline below.
      </p>

      <h4 style={styles.subSubHeader}>Start with Recent Alumni</h4>
      <p>
        The easiest and least stressful way to learn about your industry and
        meet kind like-minded people is to reach out to recent alumni of your
        school that work at places you are interested in. These are two tips in
        one reach out to alumni and reach out to juniors <strong>first</strong>.
      </p>
      <p>
        Reach out to alumni as they are much more willing to help you. Alumni
        have a tie to the school and want to help out people that were in their
        shoes. Your best bet for a helpful call is to find someone from your
        school.
      </p>
      <p>
        Starting your networking journey with more junior people is also very
        key. I made sure to reach out to juniors even at places I wasn’t super
        interested in working at just so I could get practice, giving my
        elevator pitch, asking insightful questions, and understanding the flow
        of a 15-30 minute phone call with a random person. Juniors are much more
        likely to let mistakes slip and help you out.
      </p>

      <h3 style={styles.subSubHeader}>Prepare Beforehand</h3>
      <p>
        Prepping for just 10 minutes before you get on the call with someone can
        make all the difference. No one wants to talk to someone who hasn’t done
        the basic courtesy of doing a little homework. You asked for their time,
        and you should make sure to use it effectively and leave them feeling
        like they were helpful. If they have to spend time explaining where they
        work and their role, you have not done enough homework.
      </p>
      <p>
        LinkedIn is great for this because many people have their work history
        spelled out for you, but if they are more senior it might require some
        more digging. Sometimes “doing your homework” might mean talking to
        others within the bank or juniors on their team. If you had met everyone
        on their team before them, you would have a lot more context to ask
        questions.
      </p>

      <h3 style={styles.subSubHeader}>Send the Right Message</h3>
      <p>
        Below is a sample message that you could send on LinkedIn for free
        today. Unfortunately, LinkedIn has scaled back its message requests and
        you can only send 3 for free. Your best bet is to try premium for free
        for 1 month and send as many as you can in that window.
      </p>
      <p>
        As I mentioned I had the premium free trial and sent over{" "}
        <strong>600</strong>, so it's possible.
      </p>
      <p>
        This message is short to fit within the 200-character limit. With
        premium, you should get bumped to 300. I have also included actual
        messages I sent to people during my networking sprint.
      </p>
      <FigureGrid
        images={[
          {
            src: image1,
            alt: "Jack Pearson LinkedIn Message Sample",
            title: "Free Message Sample",
          },
          {
            src: image2,
            alt: "Jack Pearson LinkedIn Message Sample",
            title: "Message Sample CFO",
          },
          {
            src: image3,
            alt: "Jack Pearson LinkedIn Message Sample",
            title: "Message Sample Getting Recruiter Contact",
          },
          {
            src: image4,
            alt: "Jack Pearson LinkedIn Message Sample",
            title: "Message Sample MD",
          },
        ]}
      />
      <p>
        It is also important to note that LinkedIn is not the only method of
        contact. You may need to send more LinkedIn requests versus emails.
        People check email far more frequently than LinkedIn. You might use
        LinkedIn to find someone on a team, then try to find their email online
        or with a new AI tool. I took the LinkedIn route but might try this in
        the future for other roles.
      </p>
      <p style={styles.note}>
        Make sure your profile is clean. People will be much more likely to talk
        to you if you present yourself professionally.
      </p>
      <h3 style={styles.subSubHeader}>You must network to do well</h3>
      <p>
        The skill of networking is key and if you develop it early it will allow
        you to go much further much quicker than your peers. It doesn’t stop
        once you get the internship offer as well. During my internship, I have
        90 coffee chats with peers at the bank. That is over 10 per week for the
        9 weeks I was on the desk. If you have your questions down and you have
        developed the skill it won’t be very hard. (I sent over 100 emails to
        colleges during my internship asking to meet)
      </p>

      <h3 style={styles.subSubHeader}>Play the “Student Card”</h3>
      <p>
        While you are in college play the student card as much as you can. What
        do I mean by this? Act like you are only interested in learning and that
        you don’t want or need anything from them. Don’t ask desperate for an
        internship offer but act curious and always be inquisitive about their
        role and work. This continues into your internship as you are “just an
        intern” and here to learn. Use this to your advantage to get time with
        people who normally would never talk to you.
      </p>
      <h2 style={styles.subHeader}>Preparing for the Interview</h2>
      <p>
        Now this section assumes you have landed a superday or equivalent
        interview for the internship. I will go through how I got mine quickly
        and then give actionable tips for how to prep. As I mentioned I was
        fortunate to get into a sophomore summit. During the second day of the
        summit, they did a group interview that acted as your first round
        screen. If you asked questions and presented yourself well you would be
        selected for a super day the following week. I will not go into a ton of
        detail about superdays and what they are as Google has plenty of
        resources. Instead, I will share my prep and what it entailed.
      </p>

      <h4 style={styles.subSubHeader}>Interview Prep Overview</h4>

      <h4 style={styles.subSubHeader}>1. Research/Study</h4>
      <p>
        First and most important is independent research and study. Since my
        interview was for sales and trading I had a more holistic approach to
        study than some other positions. For investment banking or consulting
        you may have to work through a book or do case studies, but for my role
        I studied the market and attempted to form an opinion on the current
        outlook. This entailed watching as much news as I could get my hands on
        and taking notes. I watched hours of Bloomberg and read reports
        detailing what was coming and any historical trends or events.
      </p>
      <p>
        I also prepped on the bank itself and its outlook. I read all the recent
        blog posts and reports on their website so much so that my interviewer
        said “Wow that is almost our exact thoughts here it's like you read the
        reports” which I did.
      </p>
      <p>
        I was also fortunate that my networking came in handy as complex topics
        like the crash of Silicon Valley Bank which was headline news at the
        time was explained to me in detail to prepare for any interview
        questions about the topic.
      </p>
      <p>
        Overall I researched the market, studied the bank and its role, and
        prepped any relevant question topics that could come up. I made sure to
        have an opinion not just regurgitate things to show deeper
        understanding.
      </p>

      <h4 style={styles.subSubHeader}>2. Question Prep</h4>
      <p>
        The question prep was focused on prepping specific interview questions
        for the role. Simply looking up “Sales and Trading Interview Questions”
        did the trick for me and I had a hundred to look through.
      </p>
      <p>
        There are two types of questions you need to be prepared for in most
        interviews, behavioral and technical.
      </p>
      <p>
        Behavioral questions are more about your story and personality. Again
        look up examples but in order to prep for these you need to have 5-10
        good stories that you can pull out and use for a variety of questions.
        These stories should be created using the STAR method.{" "}
        <CustomLink
          href="https://capd.mit.edu/resources/the-star-method-for-behavioral-interviews/"
          target="_blank"
          rel="noreferrer"
        >
          Check out MIT’s guide for STAR Questions
        </CustomLink>{" "}
      </p>
      <p>
        Technical questions can vary by job function and industry. For my role,
        I again google typical questions and make sure I have answers. For
        example I had a stock pitch prepared, and I knew what common indexes and
        bonds were currently trading at, and I could explain concepts related to
        trading (inverse yield curve, bond pricing, options, etc).
      </p>
      <p>
        Sometimes you will get brain teasers that can really throw you off. Your
        best bet for these is to practice, but also just have a thought process
        and use it to the best of your ability. Try to work out the problem
        logically and many times that is enough. If you don’t know a question do
        your best but don’t be afraid to say you aren’t sure and you will get
        back to them. That is a great way to follow up and give more detail on
        that question (it is better to answer everything correctly though)
      </p>

      <h4 style={styles.subSubHeader}>3. Mock Interview</h4>
      <p>
        Finally, for acing your interview it is best to do some mock interviews.
        Luckily at my school, we had a career center that had this option and I
        booked one with an upperclassman who landed a full-time role in the same
        role I was interviewing for. She had already gone through all the
        processes we had gone over and was able to give me some real questions
        she had asked just a year before. If you don’t have this luxury get a
        friend to read off some questions, talk to an AI, or just record
        yourself answering common interview questions. It is important to get a
        few reps and solidify all your prep into coherent answers.
      </p>
      <h2 style={styles.subHeader}>
        How to get the offer during the internship
      </h2>
      <p>
        Assuming you followed all the advice above and put your best efforts in
        on the interview, you might have been lucky enough to secure an
        internship offer and are getting ready to start. Here are the best tips
        that I attribute to landing a full-time offer.
      </p>

      <h4 style={styles.subSubHeader}>1. Be early stay late</h4>
      <p>
        I was the first one in and the last one out for my internship whenever
        possible. This was the single biggest key for me and allowed me to show
        in real time my work ethic and dedication. When you stay longer than
        everyone they begin to respect you almost immediately. It also allows
        you to expand your work into longer time chunks. As an intern you don’t
        get a ton of work right away, so working late allows you to be slow and
        develop the skills at your own pace. If you are cramming work to try to
        leave at 5 pm you will most likely provide worse results than the guy
        who took his time. This practice also says a lot without saying
        anything, you show don’t tell and your work speaks for itself.
      </p>
      <h4 style={styles.subSubHeader}>2. Be proactive</h4>
      <p>
        Another key to the internship is being proactive. This includes doing
        things without being explicitly told, giving yourself things to work on,
        and asking for ways to help and projects. For me I was given a few
        projects to work on, and those I did to the absolute best of my ability.
        Once I got a handle on those I began to ask for other things to do or
        ask more junior people for ways to help them. Being proactive also just
        means listening and thinking of ways to help. If in doubt ask your
        direct superior how to help them and just start there.
      </p>

      <h4 style={styles.subSubHeader}>3. Be professional</h4>
      <p>
        This seems simple but it is one of the keys to being successful.
        Remember you are a professional and you should do your best to act like
        one. This means being respectful, being timely, and maintaining
        composure. I like to think of my email as a digital footprint of how I
        was as an employee. I always write professional emails and treat
        everyone I come in contact with respectfully. I also show up looking
        professional (or at least as professional as I can lol) and present
        myself in a respectable way. As interns, we are still in college and can
        sometimes get drawn into drama or silly things with others but it is
        best to avoid this and focus on the task at hand, getting a job.
      </p>

      <h2 style={styles.subHeader}>Final Thoughts</h2>

      <p>
        I hope you found some of this helpful. I tried to write a fully
        comprehensive guide to how I landed my full-time role and how anyone in
        the same position could land a role as well. To wrap things up I think
        it is important to remember this is your career, your life, your story,
        no one is gonna write it for you. You have to take ownership and put as
        much in as you want to get out. I got a satisfactory result because I
        put in sufficient effort to make that happen. I came from a non-target
        school and landed and really awesome post-grad job with a great salary.
        You can too it just takes effort. Good luck
      </p>

      <p>- Jack</p>
      <p style={styles.date}>Published on June 25, 2025</p>

      <Link to="/blogs" style={styles.backLink}>
        Back to Blogs
      </Link>
    </div>
  );
};

export default EquityResearchBlog;
